import React, { useState } from 'react';
import { Menu, X } from 'lucide-react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useAuthForm } from '../../hooks/useAuthForm';
import { LanguageSelector } from '../LanguageSelector';
import { useScrollHeader } from '../../hooks/useScrollHeader';
import { useTranslation } from '../../hooks/useTranslation';

export default function Navbar() {
  const { user } = useAuth();
  const { signOut } = useAuthForm();
  const navigate = useNavigate();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const isVisible = useScrollHeader();
  const { t } = useTranslation();

  const handleFeaturesClick = (e: React.MouseEvent) => {
    e.preventDefault();
    // Si on est déjà sur la page d'accueil, scroll vers la section
    if (window.location.pathname === '/') {
      const featuresSection = document.getElementById('features');
      if (featuresSection) {
        featuresSection.scrollIntoView({ behavior: 'smooth' });
      }
    } else {
      // Sinon, naviguer vers la page d'accueil avec un paramètre pour scroller
      navigate('/?scrollTo=features');
    }
    setIsMenuOpen(false);
  };

  return (
    <nav className={`fixed w-full bg-black/10 backdrop-blur-xl z-50 border-b border-white/10 transition-transform duration-300 ${
      isVisible ? 'translate-y-0' : '-translate-y-full'
    }`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          {/* Logo */}
          <Link 
            to="/" 
            className="flex items-center gap-2 hover:opacity-80 transition-opacity"
          >
            <img 
              src="https://i.imgur.com/3FXFggm.png" 
              alt="Botmaker Agency"
              className="h-8 w-auto"
            />
          </Link>

          {/* Desktop Menu */}
          <div className="hidden md:flex items-center space-x-8">
            <LanguageSelector />
            {!user ? (
              <>
                <a 
                  href="/#features" 
                  onClick={handleFeaturesClick}
                  className="text-gray-300 hover:text-white transition-colors"
                >
                  {t('footer.product.features')}
                </a>
                <Link to="/pricing" className="text-gray-300 hover:text-white transition-colors">
                  {t('footer.product.pricing')}
                </Link>
                <button 
                  onClick={() => navigate('/signin')}
                  className="bg-black/20 backdrop-blur-xl text-white px-4 py-2 rounded-xl border border-white/10 hover:border-indigo-500/50 transition-all"
                >
                  {t('auth.signIn')}
                </button>
                <button 
                  onClick={() => navigate('/signup')}
                  className="bg-gradient-to-r from-violet-600 to-cyan-500 text-white px-4 py-2 rounded-xl hover:shadow-[0_0_30px_rgba(79,70,229,0.3)] transition-all"
                >
                  {t('auth.signUp')}
                </button>
              </>
            ) : (
              <>
                <Link 
                  to="/app/dashboard" 
                  className="text-gray-300 hover:text-white transition-colors"
                >
                  {t('pages.home.hero.cta.dashboard')}
                </Link>
                <button 
                  onClick={() => signOut()}
                  className="bg-black/20 backdrop-blur-xl text-white px-4 py-2 rounded-xl border border-white/10 hover:border-indigo-500/50 transition-all"
                >
                  {t('auth.signOut')}
                </button>
              </>
            )}
          </div>

          {/* Mobile Menu Button */}
          <div className="md:hidden">
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="text-gray-300 hover:text-white p-2"
            >
              {isMenuOpen ? (
                <X className="h-6 w-6" />
              ) : (
                <Menu className="h-6 w-6" />
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      <div 
        className={`md:hidden bg-black/95 backdrop-blur-xl border-t border-white/10 transition-all duration-300 ease-in-out ${
          isMenuOpen ? 'block' : 'hidden'
        }`}
      >
        <div className="px-4 pt-2 pb-4 space-y-3">
          <div className="py-2">
            <LanguageSelector />
          </div>
          {!user ? (
            <>
              <a
                href="/#features"
                onClick={handleFeaturesClick}
                className="block text-gray-300 hover:text-white transition-colors py-2"
              >
                {t('footer.product.features')}
              </a>
              <Link
                to="/pricing"
                className="block text-gray-300 hover:text-white transition-colors py-2"
                onClick={() => setIsMenuOpen(false)}
              >
                {t('footer.product.pricing')}
              </Link>
              <button 
                onClick={() => {
                  navigate('/signin');
                  setIsMenuOpen(false);
                }}
                className="w-full bg-black/20 backdrop-blur-xl text-white px-4 py-2 rounded-xl border border-white/10 hover:border-indigo-500/50 transition-all mb-2"
              >
                {t('auth.signIn')}
              </button>
              <button 
                onClick={() => {
                  navigate('/signup');
                  setIsMenuOpen(false);
                }}
                className="w-full bg-gradient-to-r from-violet-600 to-cyan-500 text-white px-4 py-2 rounded-xl hover:shadow-[0_0_30px_rgba(79,70,229,0.3)] transition-all"
              >
                {t('auth.signUp')}
              </button>
            </>
          ) : (
            <>
              <Link
                to="/app/dashboard"
                className="block text-gray-300 hover:text-white transition-colors py-2"
                onClick={() => setIsMenuOpen(false)}
              >
                {t('pages.home.hero.cta.dashboard')}
              </Link>
              <button 
                onClick={() => {
                  signOut();
                  setIsMenuOpen(false);
                }}
                className="w-full bg-black/20 backdrop-blur-xl text-white px-4 py-2 rounded-xl border border-white/10 hover:border-indigo-500/50 transition-all"
              >
                {t('auth.signOut')}
              </button>
            </>
          )}
        </div>
      </div>
    </nav>
  );
}